import {
  deleteApiSetting,
  getApiSettings,
  insertApiSetting,
} from "API/ScannerAPI";
import ConfirmAction from "Layout/Components/ConfirmAction";
import { Component } from "react";
import { withTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";

const API_U_URL = process.env.REACT_APP_U_API_URL;

// curl that checks one URL with the given key, same request as the Verify page
function curlCommand(apiKey) {
  return `curl -X POST "${API_U_URL}/url/scan" \\
  -H "Content-Type: application/json" \\
  -H "apikey: ${apiKey}" \\
  -d '{"urls": ["https://www.emailveritas.com"], "whois": true, "rescan": false, "technical_analysis": true}'`;
}

class ScannerApiSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      apiName: "",
      apiKeys: [],
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchApiKeys();
  }

  async fetchApiKeys() {
    try {
      this.setState({ loading: true, error: null });
      const data = await getApiSettings();
      this.setState({ apiKeys: data.data || [] });
    } catch (error) {
      console.error("Error fetching API keys:", error);
      this.setState({ error: this.errorMessage(error) });
    } finally {
      this.setState({ loading: false });
    }
  }

  async handleCreateApiKey() {
    const { apiName } = this.state;
    if (!apiName.trim()) {
      this.setState({ error: this.props.t("phish_guard_url_checker.settings.name_required") });
      return;
    }
    try {
      this.setState({ loading: true, error: null });
      // Call the API to create a new key
      const result = await insertApiSetting(apiName.trim());
      // Optionally, you can check the result for errors here.
      await this.fetchApiKeys(); // Refresh the list
      this.setState({ apiName: "" });
    } catch (error) {
      console.error("Error creating API key:", error);
      this.setState({ error: this.errorMessage(error) });
    } finally {
      this.setState({ loading: false });
    }
  }

  async handleDeleteApiKey(apiKey) {
    try {
      this.setState({ loading: true, error: null });
      await deleteApiSetting(apiKey);
      await this.fetchApiKeys();
    } catch (error) {
      console.error("Error deleting API key:", error);
      this.setState({ error: this.errorMessage(error) });
    } finally {
      this.setState({ loading: false });
    }
  }

  // message shown when a request fails
  errorMessage(error) {
    const { t } = this.props;
    if (error && [401, 403, 404].includes(error.code)) {
      return t("phish_guard_url_checker.settings.not_allowed");
    }
    return (error && error.message) || t("phish_guard_url_checker.settings.request_error");
  }

  handleCopy(apiKey) {
    navigator.clipboard.writeText(apiKey);
  }

  // the most recently created key
  latestApiKey() {
    const { apiKeys } = this.state;
    if (!apiKeys || apiKeys.length === 0) {
      return null;
    }
    return [...apiKeys].sort((a, b) =>
      String(b.creationDate || b.createdAt || "").localeCompare(String(a.creationDate || a.createdAt || "")))[0];
  }

  render() {
    const { t } = this.props;
    const { apiName, apiKeys, loading, error } = this.state;
    const latest = this.latestApiKey();
    const curl = latest ? curlCommand(latest.apikey || latest.apiKey) : null;

    return (
      <div className="scanner-api-settings-container">
        <h2>{t("phish_guard_url_checker.settings.title")}</h2>
        {error && <Alert color="danger">{error}</Alert>}
        <Row className="mb-3 d-flex align-items-center">
          <Col md="4">
            <Input
              type="text"
              placeholder={t("phish_guard_url_checker.settings.input_placeholder")}
              value={apiName}
              onChange={(e) => this.setState({ apiName: e.target.value })}
              disabled={loading}
            />
          </Col>
          <Col md="4" >
            <Button
              color="primary"
              onClick={() => this.handleCreateApiKey()}
              disabled={loading}
            >
              {t("phish_guard_url_checker.settings.create_api_key")}
            </Button>
          </Col>
        </Row>
        {loading ? (
          <Spinner className="my-4" color="primary" />
        ) : (
          <Card>
            <CardBody>
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>{t("phish_guard_url_checker.settings.table.name")}</th>
                    <th>{t("phish_guard_url_checker.settings.table.created_at")}</th>
                    <th>{t("phish_guard_url_checker.settings.table.api_key")}</th>
                    <th className="text-right py-0 align-middle">
                      {t("phish_guard_url_checker.settings.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.length > 0 ? (
                    apiKeys.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.apiname || item.name}</td>
                        <td>{item.creationDate || item.createdAt}</td>
                        <td>
                          <Input type="text" readOnly value={item.apikey || item.apiKey}/>
                        </td>
                        <td className="text-right py-0 align-middle">
                          <div className="btn-group btn-group-sm">
                            <Button color="success" size="sm" className="mb-2 mr-2"
                              onClick={() => this.handleCopy(item.apikey || item.apiKey)}>
                              <span className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                                <i className="pe-7s-copy-file" />
                                {t("phish_guard_url_checker.settings.table.copy")}
                              </span>
                            </Button>
                            <ConfirmAction btnClassName="mb-2 mr-2" color="danger" title={t('actions.confirmation')}
                              message={t("phish_guard_url_checker.settings.delete_confirm", { name: item.apiname || item.name })}
                              onConfirm={() => this.handleDeleteApiKey(item.apikey || item.apiKey)}>
                                <span className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                                  <i className="pe-7s-trash" />
                                  {t("phish_guard_url_checker.settings.table.delete")}
                                </span>
                            </ConfirmAction>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        {t("phish_guard_url_checker.settings.table.no_keys")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        )}
        {!loading && curl && (
          <Card className="mt-3">
            <CardBody>
              <h5>{t("phish_guard_url_checker.settings.test_title")}</h5>
              <p className="text-muted">
                {t("phish_guard_url_checker.settings.test_description", { name: latest.apiname || latest.name })}
              </p>
              <pre className="p-3 mb-3" style={{ background: "#f4f5f7", borderRadius: "4px", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {curl}
              </pre>
              <Button color="success" size="sm" onClick={() => this.handleCopy(curl)}>
                <span className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                  <i className="pe-7s-copy-file" />
                  {t("phish_guard_url_checker.settings.copy_curl")}
                </span>
              </Button>
            </CardBody>
          </Card>
        )}
      </div>
    );
  }
}

export default withTranslation()(ScannerApiSettings);
